<script setup lang="ts">
  import { FlowLoaderWidget, FlowWidget } from '@/widgets/Flow'
  import { RouterView, useRoute, useRouter } from 'vue-router'
  import {
    computed,
    type ComputedRef,
    defineAsyncComponent,
    inject,
    onMounted,
    provide,
    ref,
    watch,
  } from 'vue'
  import { useFlow, useFlowSteps } from '@/shared/composables'
  import { useCommonStore, useUserStore } from '@/shared/store'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { TravelDirection } from '@/shared/types/common.types'
  import { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'
  import { useI18n } from 'vue-i18n'
  import { useRedirectUrl } from '@/shared/composables/useRedirectUrl/'
  import { ERROR_MESSAGES } from '@/shared/constants/errors'
  import {
    RAMADAN_BANNER_PAGES,
    RAMADAN_CAMPAIGN_2025_DEV,
    RAMADAN_CAMPAIGN_2025_PROD,
  } from '@/shared/constants/features.constants'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  import { useIsProduction } from '@/shared/composables/useIsProduction'
  import RamadanBanner from '@/shared/components/RamadanBanner/RamadanBanner.vue'
  import type { TripType } from '@/shared/store/userStore/userStore.types'
  import { useAbTest } from '@/shared/composables/useAbTest'
  import { AIRALO_TEST_KEY } from '@/shared/constants'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'

  const FlowNetworkErrorWidgetQC = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowNetworkErrorWidgetQC/FlowNetworkErrorWidgetQC.vue')
  )

  const { locale } = useI18n()
  const { flowScheme } = useFlow()
  const { currentStep, stepsCount, step } = useFlowSteps(flowScheme)
  const { query, meta, params } = useRoute()
  const { push, replace, currentRoute } = useRouter()
  const { getHomeUrl } = useHomeUrl()
  const isProduction = useIsProduction()

  const isRTL = inject('isRTL') as ComputedRef<boolean>
  const error = ref('')
  const loading = ref(false)
  const commonStore = useCommonStore()
  const userStore = useUserStore()
  const { getFeatureValue, isFirstStepQcEnabled, isProgressBarQcEnabled } = useFeaturesStore()
  const isStopBack = computed(() => commonStore.isStopBack)
  const backCallback = computed(() => commonStore.backCallback)
  const logoAffinity = ref('')
  const homeUrl = ref(getHomeUrl())

  const isRamadanCampaign = computed(() => {
    if (isProduction) {
      return getFeatureValue(RAMADAN_CAMPAIGN_2025_PROD)
    }

    return getFeatureValue(RAMADAN_CAMPAIGN_2025_DEV)
  })

  provide('isRamadanCampaign', isRamadanCampaign.value)

  const isRamadanBannerActive = computed(
    () => RAMADAN_BANNER_PAGES.includes(step.value.current) && isRamadanCampaign.value
  )

  const isOutbound = computed(() => meta.name === TravelDirection.OUTBOUND)

  const nextStep = async (nextStep?: AvailableFlowRoutes) => {
    await push({ name: nextStep ?? step.value.next })
  }

  const getAffinityData = async () => {
    const affinityNameFromStorage = JSON.parse(
      String(sessionStorage.getItem('travel_affinity-code'))
    )?.code
    const affinityName = affinityNameFromStorage ?? query.affinity

    if (!affinityName) return

    const affinityDate = await commonStore.getAffinityData(affinityName)
    userStore.setPromocode(affinityDate.promoCode, true)
    logoAffinity.value = affinityDate.logo
  }

  const resolveError = () => {
    error.value = ''
  }

  const handleOnBack = () => {
    if (isStopBack.value) {
      backCallback.value?.()
      return
    }

    resolveError()

    if (
      step.value.current === OutboundFlowRoutes.DESTINATION ||
      step.value.current === InboundFlowRoutes.RESIDENCE ||
      (step.value.current === OutboundFlowRoutes.CONTACTS && userStore.isHeroSection)
    ) {
      window.location.href = homeUrl.value
      return
    }

    push({ name: step.value.prev })
  }

  const logError = (errorMessage: unknown) => {
    error.value = errorMessage instanceof Error ? errorMessage.message : String(errorMessage)
    console.error(error.value)
  }

  const onMandatoryParametersMiss = () => {
    if (meta.name === TravelDirection.OUTBOUND) {
      window.location.href = homeUrl.value
    } else {
      push({ name: InboundFlowRoutes.RESIDENCE, params: { locale: params.locale } })
    }
  }

  const requestPool = new Set()

  const onLoading = async (
    request: () => Promise<any>,
    requestId = 'request',
    withLoader = true,
    withError = true,
    infinityLoader = false
  ) => {
    const routeFrom = currentRoute.value.fullPath
    requestPool.add(requestId)
    if (withLoader) loading.value = true

    try {
      await request()
    } catch (error) {
      if (routeFrom === currentRoute.value.fullPath && withError) {
        logError(error)
      }

      if (error === ERROR_MESSAGES.MANDATORY_PARAMS_MISS) {
        onMandatoryParametersMiss()
      }
    }

    if (infinityLoader) return
    requestPool.delete(requestId)
    if (requestPool.size > 0) return
    loading.value = false
  }

  const { getAbTest } = useAbTest()
  const { getRedirectUrl } = useRedirectUrl()

  const setHeroSectionFromRoute = () => {
    if (
      !(
        query.destination &&
        query.departureDate &&
        query.returnDate &&
        query.adultsCount &&
        query.childrenCount &&
        query.tripType
      )
    ) {
      return
    }

    const destination: { id: string; name: string }[] = JSON.parse(query.destination as string)
    const departureDate = new Date(query.departureDate as string)
    const returnDate = new Date(query.returnDate as string)
    const adultsCount = Number(query.adultsCount as string)
    const childrenCount = Number(query.childrenCount as string)
    const tripType = query.tripType as TripType

    userStore.setDestination(destination, true)
    userStore.setTravelDates(departureDate, returnDate, true)
    userStore.setTravelersCount(adultsCount, childrenCount, true)
    userStore.setTripType(tripType, true)
    userStore.setIsHeroSection(true, true)
  }

  onMounted(async () => {
    setHeroSectionFromRoute()
    userStore.setRedirectURL(getRedirectUrl(locale.value))
    if (meta.name === TravelDirection.OUTBOUND) {
      await getAbTest(AIRALO_TEST_KEY)
      await onLoading(getAffinityData, 'getAffinityData', !isFirstStepQcEnabled)
    }
  })

  watch(
    currentRoute,
    () => {
      resolveError()
      const stepIndex = flowScheme.findIndex((stepName) => stepName === currentRoute.value.name)
      if (stepIndex >= 0) {
        currentStep.value = stepIndex + 1
      } else {
        replace({ name: flowScheme[0] })
      }
    },
    { immediate: true }
  )
</script>

<template>
  <RamadanBanner v-if="isRamadanBannerActive" />
  <FlowWidget
    with-arrow-back
    :steps-count="stepsCount"
    :current-step="currentStep"
    :first-step="currentStep"
    :is-rtl="isRTL"
    :logo-affinity="logoAffinity"
    :class="{ outbound: isOutbound, rtl: isRTL }"
    class="flow-view-v2"
    @go-back="handleOnBack"
  >
    <FlowNetworkErrorWidgetQC v-if="error" :home-url="homeUrl" @resolve="resolveError" />
    <template v-else>
      <FlowLoaderWidget v-show="loading" />
      <RouterView
        v-show="!loading"
        :loading="loading"
        :with-back-button="isProgressBarQcEnabled"
        @proceed="nextStep"
        @load="onLoading"
        @back="handleOnBack"
      />
    </template>
  </FlowWidget>
</template>
